<template>
  <v-container class="pa-0 ma-0" fluid>
    <v-card elevation="0" min-width="100%">
      <common-data-table
        :headers="headers"
        collectionName="workforce"
        ref="editTable"
        @click:row="rowClicked"
      >
        <template v-slot:newItemPopup="{ close, item, formKey }">
          <common-editing-popup
            ref="editPopup"
            :title="item ? 'Edit employee' : 'New employee'"
            :saveBtnTitle="item ? 'Save employee' : 'Create employee'"
            @close="close()"
            :canSubmit="formValid"
            collectionName="workforce"
            @submit="addWorkforce"
            :loading="addingWorkforce"
            :originalItem="item"
            :useSetForData="true"
            :key="'employee-edit-' + (item ? item.id : formKey)"
          >
            <template v-slot:default="{ editedItem, extraData }">
              <new-employee
                @formStateChange="formStateChange"
                :editedItem="editedItem"
                :extraData="extraData"
              ></new-employee>
            </template>
          </common-editing-popup>
        </template>
      </common-data-table>
    </v-card>
  </v-container>
</template>

<script>
import CommonDataTable from '@/components/CommonDataTable.vue'
import CommonEditingPopup from '@/components/CommonEditingPopup.vue'
import NewEmployee from '@/views/people/NewEmployee.vue'
import { functions, storage } from '@/firebaseConfig'
import { mapGetters, mapActions } from 'vuex'
import {
  hasAccessRightForCollection,
  AccessRightWrite,
} from '@/plugins/feature'
import { v4 as uuidv4 } from 'uuid'

export default {
  name: 'Workforce',
  components: {
    CommonDataTable,
    CommonEditingPopup,
    NewEmployee,
  },
  props: ['addNewPopupVisible'],
  data() {
    return {
      addingWorkforce: false,
      formValid: false,
      headers: [
        {
          text: 'Employee name',
          value: 'name',
          merge: {
            values: ['firstName', 'lastName'],
          },
        },
        {
          text: 'Job title',
          value: 'jobTitle',
        },
        {
          text: 'Email address',
          value: 'email',
        },
      ],
      rules: {
        required: (value) => !!value || 'Required',
        email: (value) => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || 'Invalid e-mail.'
        },
      },
    }
  },
  computed: {
    ...mapGetters(['companyId']),
    isAdmin() {
      return hasAccessRightForCollection('workforce', AccessRightWrite)
    },
  },
  mounted() {
    this.$parent.$on('addWorkforce', () => this.addWorkforce())
  },
  methods: {
    ...mapActions(['showError', 'showSuccess']),
    getPopupKey() {
      return uuidv4()
    },
    formStateChange(newValue) {
      this.formValid = newValue
    },
    afterDropzoneCopmplete(upload) {
      this.imageFile = upload
    },
    getImageRefFromURL(imageURL) {
      const imageURLRef = unescape(/.*\/([^?]+)/.exec(imageURL)[1])
      return imageURLRef
    },
    rowClicked(data) {
      if (this.isAdmin) {
        this.$router.push({ name: 'profile-personal', params: { id: data.id } })
      }
    },
    async deleteRemovedImages(newItem, extraData) {
      if (newItem.id) {
        if (
          (newItem.avatarURL && extraData?.imageFile?.file) ||
          extraData?.imageToRemove
        ) {
          const ref = this.getImageRefFromURL(extraData.imageToRemove)
          const imageRef = storage.ref().child(ref)
          await imageRef.delete()
          delete newItem.avatarURL
        }
      }
    },
    async uploadNewImages(item, extraData) {
      const file = extraData?.imagePreview?.file
      if (file) {
        const fileName = uuidv4()
        const imageRef = storage
          .ref()
          .child(`companies/${this.companyId}/workforce/${fileName}`)
        const snapshot = await imageRef.put(file)
        const fileURL = await snapshot.ref.getDownloadURL()
        item.avatarURL = fileURL
      }
    },

    async addWorkforce(newItem, extraData) {
      this.addingWorkforce = true
      try {
        if (newItem.id) {
          // updating data only

          await this.deleteRemovedImages(newItem, extraData)
          await this.uploadNewImages(newItem, extraData)

          const updateWorkforceFunc = functions.httpsCallable(
            'people-updateWorkforce'
          )

          const data = Object.assign({}, newItem)
          data.uid = newItem.id
          data.companyId = this.companyId

          await updateWorkforceFunc(data)

          this.$refs.editTable.closeEditItemPopup()

          this.showSuccess('Employee has been updated')
        } else {
          newItem.companyId = this.companyId

          const addWorkforceFunc = functions.httpsCallable(
            'people-addWorkforce'
          )
          await this.uploadNewImages()

          const data = Object.assign({}, newItem)
          await addWorkforceFunc(data)

          this.$refs.editTable.closeEditItemPopup()
          this.showSuccess('Employee has been added')
        }
      } catch (error) {
        console.error(error)
        this.showError(error)
      } finally {
        this.addingWorkforce = false
      }
    },
  },
}
</script>
